import { LayoutGrid, Rows3 } from "lucide-react";
import { useGetIdentity, useListContext, useTranslate } from "ra-core";
import { CreateButton } from "@/components/admin/create-button";
import { FilterButton } from "@/components/admin/filter-form";
import { List } from "@/components/admin/list";
import { ListPagination } from "@/components/admin/list-pagination";
import { SortButton } from "@/components/admin/sort-button";

import { DataImportButton } from "../dataImport/DataImportButton";
import { TopToolbar } from "../layout/TopToolbar";
import { useCompanyColumns } from "../spreadsheet/companyColumns";
import { ExcelExportButton } from "../spreadsheet/ExcelExportButton";
import { InitialDisplayedFilters } from "../filters/InitialDisplayedFilters";
import { KnownFiltersOnly } from "../filters/KnownFiltersOnly";
import { ListViewToggle } from "../misc/ListViewToggle";
import { useListView } from "../misc/useListView";
import { CompanyEmpty } from "./CompanyEmpty";
import { DEFAULT_COMPANY_FILTERS, useCompanyFilters } from "./companyFilters";
import { CompanyTable } from "./CompanyTable";
import { ImageList } from "./GridList";

type CompanyListView = "table" | "cards";

const VIEW_STORE_KEY = "companies.listView";
const DEFAULT_VIEW: CompanyListView = "table";

const useCompanyListView = () =>
  useListView<CompanyListView>(VIEW_STORE_KEY, DEFAULT_VIEW);

export const CompanyList = () => {
  const { identity } = useGetIdentity();
  const translate = useTranslate();
  const filters = useCompanyFilters();
  if (!identity) return null;
  return (
    <List
      title={translate("resources.companies.name", { smart_count: 2 })}
      perPage={25}
      sort={{ field: "name", order: "ASC" }}
      filters={filters}
      actions={<CompanyListActions />}
      pagination={<ListPagination rowsPerPageOptions={[10, 25, 50, 100]} />}
    >
      <KnownFiltersOnly filters={filters} />
      <CompanyListLayout />
    </List>
  );
};

const CompanyListLayout = () => {
  const { data, isPending, filterValues } = useListContext();
  const [view] = useCompanyListView();
  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (isPending) return null;
  if (!data?.length && !hasFilters) return <CompanyEmpty />;

  return (
    <div className="flex flex-col gap-4 min-w-0">
      <InitialDisplayedFilters sources={DEFAULT_COMPANY_FILTERS} />
      {view === "cards" ? <ImageList /> : <CompanyTable />}
    </div>
  );
};

const CompanyListActions = () => {
  const translate = useTranslate();
  const [view] = useCompanyListView();
  const columns = useCompanyColumns();
  return (
    <TopToolbar>
      <ListViewToggle<CompanyListView>
        storeKey={VIEW_STORE_KEY}
        defaultView={DEFAULT_VIEW}
        options={[
          {
            value: "table",
            icon: <Rows3 className="h-4 w-4" />,
            label: "crm.common.view_table",
          },
          {
            value: "cards",
            icon: <LayoutGrid className="h-4 w-4" />,
            label: "crm.common.view_cards",
          },
        ]}
      />
      {view === "cards" && (
        <SortButton fields={["name", "created_at", "nb_contacts"]} />
      )}
      <FilterButton />
      <DataImportButton resource="companies" />
      <ExcelExportButton columns={columns} />
      <CreateButton
        label={translate("resources.companies.action.new", {
          _: "New Company",
        })}
      />
    </TopToolbar>
  );
};
